import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Geography & Maps — 10 templates
   world_map, rotating_globe, continent_outline, compass_rose,
   topographic_map, route_path, city_grid, terrain_cross_section,
   ocean_current, climate_zone
   ═══════════════════════════════════════════════════════════════ */

// ── helpers ──────────────────────────────────────────────────
const geoGrad = (id: string, c1: string, c2: string) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={c1} />
    <stop offset="100%" stopColor={c2} />
  </linearGradient>
);
const geoRadial = (id: string, c1: string, c2: string) => (
  <radialGradient id={id} cx="50%" cy="40%" r="55%">
    <stop offset="0%" stopColor={c1} />
    <stop offset="100%" stopColor={c2} />
  </radialGradient>
);

/* ─── 1. World Map ────────────────────────────────────────── */
export const WorldMap: React.FC<Record<string, unknown>> = (props) => {
  const highlight = (props.highlightTarget as string) || "";
  const activeRegion = (props.activeRegion as string) || "";
  const label = (props.label as string) || "World Map";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("wm-ocean", "#1a5276", "#0e2f44")}
        {geoGrad("wm-land", "#27ae60", "#1e8449")}
        <filter id="wm-shadow"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#wm-ocean)" rx="16" />
      {/* Simplified continent shapes */}
      <g filter="url(#wm-shadow)">
        {/* North America */}
        <path d="M120,140 Q160,100 220,110 L250,160 Q240,220 200,260 L160,240 Q130,200 120,140Z"
          fill={activeRegion === "north_america" ? "#f39c12" : "url(#wm-land)"}
          opacity={highlight === "north_america" ? 1 : 0.85} />
        {/* South America */}
        <path d="M200,280 Q230,270 250,300 L260,380 Q240,440 220,460 L200,420 Q180,360 190,300Z"
          fill={activeRegion === "south_america" ? "#f39c12" : "url(#wm-land)"}
          opacity={highlight === "south_america" ? 1 : 0.85} />
        {/* Europe */}
        <path d="M380,110 Q420,90 460,100 L470,140 Q450,170 420,160 L390,150Z"
          fill={activeRegion === "europe" ? "#f39c12" : "url(#wm-land)"}
          opacity={highlight === "europe" ? 1 : 0.85} />
        {/* Africa */}
        <path d="M400,180 Q440,170 470,200 L480,300 Q460,380 430,400 L400,360 Q380,280 390,210Z"
          fill={activeRegion === "africa" ? "#f39c12" : "url(#wm-land)"}
          opacity={highlight === "africa" ? 1 : 0.85} />
        {/* Asia */}
        <path d="M480,80 Q540,60 620,90 L660,160 Q640,220 580,240 L520,220 Q490,180 480,130Z"
          fill={activeRegion === "asia" ? "#f39c12" : "url(#wm-land)"}
          opacity={highlight === "asia" ? 1 : 0.85} />
        {/* Oceania */}
        <path d="M600,340 Q640,320 680,340 L690,380 Q670,410 640,400 L610,380Z"
          fill={activeRegion === "oceania" ? "#f39c12" : "url(#wm-land)"}
          opacity={highlight === "oceania" ? 1 : 0.85} />
      </g>
      {/* Grid lines */}
      {[150, 250, 350, 450].map((y) => (
        <line key={y} x1="40" y1={y} x2="760" y2={y} stroke="#ffffff15" strokeDasharray="8,6" />
      ))}
      {[200, 400, 600].map((x) => (
        <line key={x} x1={x} y1="40" x2={x} y2="560" stroke="#ffffff15" strokeDasharray="8,6" />
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Rotating Globe ───────────────────────────────────── */
export const RotatingGlobe: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) || 0;
  const label = (props.label as string) || "Globe";
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoRadial("globe-sph", "#5dade2", "#1a5276")}
        <radialGradient id="globe-shine" cx="35%" cy="30%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <clipPath id="globe-clip"><circle cx="400" cy="280" r="200" /></clipPath>
        <filter id="globe-glow"><feDropShadow dx="0" dy="4" stdDeviation="12" floodColor={accentColor} floodOpacity="0.4" /></filter>
      </defs>
      <rect width="800" height="600" fill="#0a1628" rx="16" />
      {/* Stars */}
      {[{x:80,y:60},{x:200,y:40},{x:650,y:80},{x:720,y:150},{x:100,y:500},{x:680,y:480},{x:350,y:30},{x:550,y:50}].map((s,i) => (
        <circle key={i} cx={s.x} cy={s.y} r={1.5} fill="#ecf0f1" opacity={0.6 + (i % 3) * 0.15} />
      ))}
      <g filter="url(#globe-glow)">
        <circle cx="400" cy="280" r="200" fill="url(#globe-sph)" />
        {/* Continent shapes clipped to globe, rotating */}
        <g clipPath="url(#globe-clip)">
          <g transform={`rotate(${rotation}, 400, 280)`}>
            <path d="M320,180 Q360,150 400,170 L410,220 Q390,250 360,240Z" fill="#27ae60" opacity="0.8" />
            <path d="M430,160 Q480,140 520,170 L530,230 Q500,260 460,240Z" fill="#27ae60" opacity="0.8" />
            <path d="M350,290 Q380,280 400,310 L390,370 Q370,390 350,360Z" fill="#27ae60" opacity="0.75" />
            <path d="M440,300 Q470,290 490,320 L480,360 Q460,370 440,340Z" fill="#27ae60" opacity="0.75" />
          </g>
          {/* Longitude lines */}
          {[-60, -20, 20, 60].map((off) => (
            <ellipse key={off} cx={400 + off} cy="280" rx={4} ry="200" fill="none"
              stroke="#ffffff15" strokeWidth="0.8" transform={`rotate(${rotation * 0.3}, 400, 280)`} />
          ))}
          {/* Latitude lines */}
          {[200, 240, 280, 320, 360].map((y) => (
            <line key={y} x1="200" y1={y} x2="600" y2={y} stroke="#ffffff12" strokeWidth="0.8" />
          ))}
        </g>
        {/* Specular shine */}
        <circle cx="400" cy="280" r="200" fill="url(#globe-shine)" />
      </g>
      {/* Stand */}
      <ellipse cx="400" cy="500" rx="80" ry="12" fill="#2c3e50" />
      <rect x="394" y="480" width="12" height="22" rx="3" fill="#34495e" />
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Continent Outline ────────────────────────────────── */
export const ContinentOutline: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Continent";
  const accentColor = (props.accentColor as string) || "#2ecc71";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const regions = (props.regions as string[]) || ["Region A", "Region B", "Region C", "Region D"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("cont-bg", "#1a2632", "#0d1b2a")}
        <filter id="cont-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#cont-bg)" rx="16" />
      <g filter="url(#cont-sh)" transform="translate(120,60)">
        {/* 4 stylized regions */}
        {[
          { d: "M60,80 Q120,40 200,60 L220,140 Q180,180 120,160Z", cx: 140, cy: 110 },
          { d: "M220,60 Q300,30 380,70 L400,150 Q360,190 280,170Z", cx: 300, cy: 110 },
          { d: "M100,200 Q160,170 240,190 L260,280 Q220,320 140,300Z", cx: 170, cy: 240 },
          { d: "M260,190 Q340,160 420,200 L440,300 Q380,350 300,320Z", cx: 340, cy: 250 },
        ].map((r, i) => (
          <g key={i}>
            <path d={r.d}
              fill={i === activeIndex ? accentColor : "#34495e"}
              stroke={i === activeIndex ? "#fff" : "#546e7a"}
              strokeWidth={i === activeIndex ? 2.5 : 1}
              opacity={i === activeIndex ? 1 : 0.7} />
            <text x={r.cx} y={r.cy} textAnchor="middle" fill="#ecf0f1" fontSize="13" fontWeight="500">
              {regions[i] || `Region ${i + 1}`}
            </text>
          </g>
        ))}
      </g>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Compass Rose ─────────────────────────────────────── */
export const CompassRose: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) || 0;
  const label = (props.label as string) || "Compass";
  const accentColor = (props.accentColor as string) || "#e74c3c";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoRadial("comp-bg", "#2c3e50", "#1a252f")}
        <filter id="comp-sh"><feDropShadow dx="0" dy="3" stdDeviation="6" floodOpacity="0.4" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#comp-bg)" rx="16" />
      <g transform={`translate(400,280) rotate(${rotation})`} filter="url(#comp-sh)">
        <circle r="180" fill="#1c2833" stroke="#546e7a" strokeWidth="3" />
        <circle r="170" fill="none" stroke="#546e7a55" strokeWidth="1" />
        {/* Cardinal points */}
        <polygon points="0,-160 -20,-40 0,-60 20,-40" fill={accentColor} /> {/* N */}
        <polygon points="0,160 -20,40 0,60 20,40" fill="#bdc3c7" /> {/* S */}
        <polygon points="-160,0 -40,-20 -60,0 -40,20" fill="#bdc3c7" /> {/* W */}
        <polygon points="160,0 40,-20 60,0 40,20" fill="#bdc3c7" /> {/* E */}
        {/* Intercardinal */}
        {[45, 135, 225, 315].map((a) => (
          <line key={a} x1="0" y1="0" x2={Math.sin(a * Math.PI / 180) * 120}
            y2={-Math.cos(a * Math.PI / 180) * 120} stroke="#546e7a" strokeWidth="1.5" />
        ))}
        {/* Degree ticks */}
        {Array.from({ length: 36 }, (_, i) => i * 10).map((deg) => (
          <line key={deg} x1={Math.sin(deg * Math.PI / 180) * 155} y1={-Math.cos(deg * Math.PI / 180) * 155}
            x2={Math.sin(deg * Math.PI / 180) * 165} y2={-Math.cos(deg * Math.PI / 180) * 165}
            stroke="#7f8c8d" strokeWidth={deg % 90 === 0 ? 2 : 0.8} />
        ))}
        <circle r="8" fill={accentColor} />
      </g>
      {/* Labels outside rotation */}
      <text x="400" y="80" textAnchor="middle" fill={accentColor} fontSize="22" fontWeight="700">N</text>
      <text x="400" y="500" textAnchor="middle" fill="#bdc3c7" fontSize="18" fontWeight="600">S</text>
      <text x="200" y="285" textAnchor="middle" fill="#bdc3c7" fontSize="18" fontWeight="600">W</text>
      <text x="600" y="285" textAnchor="middle" fill="#bdc3c7" fontSize="18" fontWeight="600">E</text>
      <text x="400" y="565" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Topographic Map ──────────────────────────────────── */
export const TopographicMap: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Topography";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("topo-bg", "#1b2631", "#0d1117")}
      </defs>
      <rect width="800" height="600" fill="url(#topo-bg)" rx="16" />
      {/* Contour lines */}
      {[
        { rx: 280, ry: 200, color: "#1a5276" },
        { rx: 240, ry: 170, color: "#1f6f8b" },
        { rx: 200, ry: 140, color: "#27ae60" },
        { rx: 160, ry: 110, color: "#2ecc71" },
        { rx: 120, ry: 80, color: "#f39c12" },
        { rx: 80, ry: 50, color: "#e67e22" },
        { rx: 40, ry: 25, color: "#e74c3c" },
      ].map((c, i) => (
        <ellipse key={i} cx="400" cy="300" rx={c.rx} ry={c.ry}
          fill="none" stroke={i === activeIndex ? "#fff" : c.color}
          strokeWidth={i === activeIndex ? 3 : 1.5}
          opacity={i === activeIndex ? 1 : 0.7}
          transform={`rotate(${-15 + i * 3}, 400, 300)`} />
      ))}
      {/* Elevation labels */}
      {["0m", "200m", "500m", "800m", "1200m", "1800m", "2500m"].map((el, i) => (
        <text key={i} x={400 + (280 - i * 40)} y={300} fill="#ecf0f1" fontSize="10" opacity="0.6">{el}</text>
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Route Path ───────────────────────────────────────── */
export const RoutePath: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Route";
  const flowProgress = (props.flowProgress as number) || 0;
  const stops = (props.stops as string[]) || ["Start", "Waypoint 1", "Waypoint 2", "Destination"];
  const accentColor = (props.accentColor as string) || "#e74c3c";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("rp-bg", "#1e3a2f", "#0d1f17")}
        <filter id="rp-sh"><feDropShadow dx="1" dy="2" stdDeviation="3" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#rp-bg)" rx="16" />
      {/* Road */}
      <path d="M100,500 Q200,400 300,350 Q400,300 500,250 Q600,200 700,120"
        fill="none" stroke="#546e7a" strokeWidth="8" strokeLinecap="round" />
      {/* Progress overlay */}
      <path d="M100,500 Q200,400 300,350 Q400,300 500,250 Q600,200 700,120"
        fill="none" stroke={accentColor} strokeWidth="8" strokeLinecap="round"
        strokeDasharray="800" strokeDashoffset={800 - flowProgress * 800} />
      {/* Stop markers */}
      {[
        { x: 100, y: 500 }, { x: 300, y: 350 }, { x: 500, y: 250 }, { x: 700, y: 120 },
      ].map((s, i) => (
        <g key={i} filter="url(#rp-sh)">
          <circle cx={s.x} cy={s.y} r="14"
            fill={flowProgress >= i / (stops.length - 1) ? accentColor : "#2c3e50"}
            stroke="#ecf0f1" strokeWidth="2" />
          <text x={s.x} y={s.y + 35} textAnchor="middle" fill="#ecf0f1" fontSize="13" fontWeight="500">
            {stops[i] || `Stop ${i + 1}`}
          </text>
        </g>
      ))}
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. City Grid ────────────────────────────────────────── */
export const CityGrid: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "City Layout";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("cg-bg", "#1c2833", "#0d1117")}
        <filter id="cg-sh"><feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.4" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#cg-bg)" rx="16" />
      {/* Grid streets */}
      {[140, 260, 380, 500, 620].map((x) => (
        <rect key={`v${x}`} x={x - 4} y="60" width="8" height="460" fill="#2c3e50" rx="2" />
      ))}
      {[140, 240, 340, 440].map((y) => (
        <rect key={`h${y}`} x="80" y={y - 4} width="640" height="8" fill="#2c3e50" rx="2" />
      ))}
      {/* Buildings */}
      {[
        { x: 160, y: 80, w: 80, h: 40, c: "#3498db", id: "office" },
        { x: 280, y: 160, w: 80, h: 60, c: "#e74c3c", id: "hospital" },
        { x: 400, y: 80, w: 100, h: 40, c: "#f39c12", id: "school" },
        { x: 520, y: 260, w: 80, h: 50, c: "#2ecc71", id: "park" },
        { x: 160, y: 260, w: 80, h: 50, c: "#9b59b6", id: "library" },
        { x: 400, y: 360, w: 100, h: 60, c: "#1abc9c", id: "station" },
      ].map((b) => (
        <g key={b.id} filter="url(#cg-sh)">
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="4"
            fill={highlightTarget === b.id ? "#fff" : b.c}
            opacity={highlightTarget === b.id ? 0.95 : 0.75}
            stroke={highlightTarget === b.id ? b.c : "none"} strokeWidth="2" />
          <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 4} textAnchor="middle" fill="#1a1a2e" fontSize="11" fontWeight="600">
            {b.id}
          </text>
        </g>
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Terrain Cross Section ────────────────────────────── */
export const TerrainCrossSection: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Terrain Layers";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const layers = (props.layers as string[]) || ["Topsoil", "Subsoil", "Bedrock", "Mantle"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("tc-sky", "#2980b9", "#1a5276")}
      </defs>
      <rect width="800" height="600" fill="url(#tc-sky)" rx="16" />
      {/* Sky with sun */}
      <circle cx="650" cy="100" r="40" fill="#f39c12" opacity="0.8" />
      {/* Mountain surface */}
      <polygon points="60,300 200,160 350,220 500,140 650,200 740,300"
        fill="#27ae60" stroke="#1e8449" strokeWidth="2" />
      {/* Underground layers */}
      {[
        { y: 300, h: 80, color: "#6d4c41", lbl: layers[0] || "Topsoil" },
        { y: 380, h: 80, color: "#8d6e63", lbl: layers[1] || "Subsoil" },
        { y: 460, h: 70, color: "#5d4037", lbl: layers[2] || "Bedrock" },
        { y: 530, h: 70, color: "#d84315", lbl: layers[3] || "Mantle" },
      ].map((l, i) => (
        <g key={i}>
          <rect x="60" y={l.y} width="680" height={l.h}
            fill={i === activeIndex ? "#f39c12" : l.color}
            stroke={i === activeIndex ? "#fff" : "#00000030"}
            strokeWidth={i === activeIndex ? 2 : 1}
            opacity={i === activeIndex ? 1 : 0.85} />
          <text x="400" y={l.y + l.h / 2 + 5} textAnchor="middle" fill="#ecf0f1" fontSize="14" fontWeight="500">{l.lbl}</text>
        </g>
      ))}
      <text x="400" y="50" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 9. Ocean Current ────────────────────────────────────── */
export const OceanCurrent: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Ocean Currents";
  const flowProgress = (props.flowProgress as number) || 0;
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("oc-bg", "#0e2f44", "#091e30")}
        <linearGradient id="oc-warm" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#e74c3c" />
          <stop offset="100%" stopColor="#f39c12" />
        </linearGradient>
        <linearGradient id="oc-cold" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#2980b9" />
          <stop offset="100%" stopColor="#1abc9c" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#oc-bg)" rx="16" />
      {/* Warm current */}
      <path d={`M80,400 Q200,${350 + Math.sin(phase) * 30} 350,${300 + Math.sin(phase * 1.2) * 20} Q500,250 700,200`}
        fill="none" stroke="url(#oc-warm)" strokeWidth="12" strokeLinecap="round" opacity="0.7"
        strokeDasharray="20,10" strokeDashoffset={-flowProgress * 100} />
      <text x="120" y="430" fill="#e74c3c" fontSize="13">Warm Current →</text>
      {/* Cold current */}
      <path d={`M700,400 Q580,${350 + Math.cos(phase) * 25} 430,${320 + Math.cos(phase * 0.8) * 20} Q300,300 100,250`}
        fill="none" stroke="url(#oc-cold)" strokeWidth="12" strokeLinecap="round" opacity="0.7"
        strokeDasharray="20,10" strokeDashoffset={-flowProgress * 100} />
      <text x="640" y="430" fill="#2980b9" fontSize="13">← Cold Current</text>
      {/* Temperature legend */}
      <rect x="320" y="500" width="160" height="12" rx="6" fill="url(#oc-warm)" opacity="0.6" />
      <text x="310" y="540" fill="#e74c3c" fontSize="11">Warm</text>
      <text x="460" y="540" fill="#f39c12" fontSize="11">Hot</text>
      <text x="400" y="575" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 10. Climate Zone ────────────────────────────────────── */
export const ClimateZone: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Climate Zones";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const zones = (props.zones as string[]) || ["Tropical", "Arid", "Temperate", "Continental", "Polar"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {geoGrad("cz-bg", "#1a2632", "#0d1117")}
      </defs>
      <rect width="800" height="600" fill="url(#cz-bg)" rx="16" />
      {/* Horizontal bands */}
      {[
        { y: 60, h: 90, color: "#e74c3c" },
        { y: 150, h: 90, color: "#f39c12" },
        { y: 240, h: 90, color: "#27ae60" },
        { y: 330, h: 90, color: "#2980b9" },
        { y: 420, h: 90, color: "#8e44ad" },
      ].map((z, i) => (
        <g key={i}>
          <rect x="100" y={z.y} width="600" height={z.h} rx="8"
            fill={i === activeIndex ? z.color : z.color}
            opacity={i === activeIndex ? 1 : 0.5}
            stroke={i === activeIndex ? "#fff" : "none"} strokeWidth="2" />
          <text x="400" y={z.y + z.h / 2 + 5} textAnchor="middle" fill="#ecf0f1" fontSize="16" fontWeight="600">
            {zones[i] || `Zone ${i + 1}`}
          </text>
          {/* Temperature indicator */}
          <text x="720" y={z.y + z.h / 2 + 5} textAnchor="start" fill="#bdc3c7" fontSize="12">
            {["35°C", "30°C", "18°C", "5°C", "-15°C"][i]}
          </text>
        </g>
      ))}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
